import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import Drawer from 'components/shared/Drawer'
import Button from 'components/ds/Button'
import Icon from 'components/ds/Icon'
import { DownloadFMCSAReport } from 'components/PdfTemplates'
import AuthService from 'tools/AuthService'
import FmcsaSummary, { DrawerHeading, FmcsaLoading, FmcsaNoDot, FmcsaNotFound } from '../../../Fmcsa/FmcsaSummary'
import ExtensiveFmcsaDetails from '../../../Fmcsa/ExtensiveFmcsaDetails'
import useExtensiveFmcsa from '../../../Fmcsa/useExtensiveFmcsa'
import { buildExtensive, buildSummary } from '../../../Fmcsa/fmcsaViewModel'

const formatDay = date => date ? moment(date).format('MMM DD YYYY') : null

const FmcsaDrawer = (props) => {
    const {
        open, onClose, applicantId, data, fmcsaSearch, dotNumber,
        loading, hasDot = true, onRequestFmcsa, requestingFmcsa, onExtensiveRequested,
    } = props

    const infoFMCSA = data?.content?.carrier
    const retrievalDate = data?.retrievalDate
    const mcNumbers = data?.extraContent?.mcNumbers
    const summary = useMemo(() => buildSummary(infoFMCSA, mcNumbers), [infoFMCSA, mcNumbers])

    const { fmcsaExtensiveSearchEnabled, creditUsageEnabled } = AuthService.getClientFeatures()
    const readOnly = AuthService.isReadOnlyUser()
    const extensiveEnabled = Boolean(open && !fmcsaSearch && applicantId && fmcsaExtensiveSearchEnabled && summary)
    const extensive = useExtensiveFmcsa({
        applicationId: applicantId,
        enabled: extensiveEnabled,
        costsEnabled: Boolean(open && !fmcsaSearch && applicantId),
        creditUsageEnabled,
        onRequested: onExtensiveRequested,
    })

    // { data, search } while the extensive view is showing
    const [extensiveView, setExtensiveView] = useState(null)
    useEffect(() => { if (!open) setExtensiveView(null) }, [open])

    const history = useHistory()

    let heading
    let actions = null
    let body

    if (extensiveView) {
        const requestedAt = extensiveView.search?.createdAt
        heading = (
            <DrawerHeading
                title="Extensive FMCSA Details"
                subtitle={requestedAt ? `Requested ${moment(requestedAt).format('MMM D, YYYY')}` : null}
                onBack={() => setExtensiveView(null)}
            />
        )
        body = (
            <ExtensiveFmcsaDetails
                extensive={buildExtensive(extensiveView.data)}
                legalName={summary?.legalName}
                dot={summary?.dot}
            />
        )
    } else {
        heading = (
            <DrawerHeading
                title="FMCSA Details"
                subtitle={loading ? 'Retrieving from FMCSA'
                    : !hasDot ? 'No DOT number on this application'
                        : retrievalDate ? `Data retrieved ${formatDay(retrievalDate)}` : null}
            />
        )
        // Download only when there is a carrier record to put in the report.
        if (summary && !loading) {
            actions = fmcsaSearch
                ? (
                    <Button startIcon={<Icon name="plus" size={18} />}
                            onClick={() => history.push(`/create-applicant?codeType=DOT&codeValue=${dotNumber}`)}>
                        Create application
                    </Button>
                )
                : (
                    <DownloadFMCSAReport applicationId={applicantId} mode='button'/>
                )
        }

        if (loading) body = <FmcsaLoading />
        else if (!hasDot) body = <FmcsaNoDot />
        else if (!summary) {
            body = (
                <FmcsaNotFound
                    dot={dotNumber}
                    onRequest={!readOnly ? onRequestFmcsa : null}
                    requesting={requestingFmcsa}
                    cost={extensive.searchCost}
                />
            )
        } else {
            body = (
                <FmcsaSummary
                    summary={summary}
                    extensive={extensiveEnabled ? extensive : null}
                    readOnly={readOnly}
                    onOpenExtensive={result => result && setExtensiveView(result)}
                />
            )
        }
    }

    return (
        <Drawer
            open={open}
            title={heading}
            onClose={onClose}
            actions={actions}
        >
            {body}
        </Drawer>
    )
}

export default FmcsaDrawer
